import { type FC, type ReactNode, Suspense } from 'react';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';

import { useRecentlyViewedItemsContext } from './RecentlyViewedItemsContext.tsx';
import {
    RecentlyViewedItemsCarousel,
    type RecentlyViewedItemsCarouselProps,
} from './RecentlyViewedItemsCarousel.tsx';
import { RecentlyViewedItemsWrapper } from './RecentlyViewedItemsWrapper.tsx';

import { ACTION_LABEL } from './constants.ts';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './RecentlyViewedItemsLazy.module.css';

type Props = Omit<RecentlyViewedItemsCarouselProps, 'queryReference'> & {
    loadingComponent?: ReactNode;
};

export const RecentlyViewedItemsLazy: FC<Props> = props => {
    const {
        actionLabel = ACTION_LABEL,
        wrapperClassName,
        trackViewMoreClick,
        isBot,
        hasTopSeparator,
        hasBottomSeparator,
        loadingComponent,
    } = props;

    const { queryReference } = useRecentlyViewedItemsContext();
    //Don't show recently viewer items module for bots
    if (isBot) {
        return null;
    }

    const defaultLoading = (
        <RecentlyViewedItemsWrapper
            wrapperClassName={wrapperClassName}
            actionLabel={actionLabel}
            trackViewMoreClick={trackViewMoreClick}
            hasTopSeparator={hasTopSeparator}
            hasBottomSeparator={hasBottomSeparator}
        >
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.justifyCenter,
                    dibsCss.itemsCenter,
                    styles.spinnerWrapper
                )}
            >
                <Spinner />
            </div>
        </RecentlyViewedItemsWrapper>
    );

    const loading = loadingComponent ?? defaultLoading;

    if (!queryReference) {
        /**
         * For this query reference we're calling loadQuery only on the client-side because of this
         * queryReference will be null on the server-side. Render loading component on the server-side.
         */
        return loading;
    }

    return (
        <Suspense fallback={loading}>
            <RecentlyViewedItemsCarousel {...props} queryReference={queryReference} />
        </Suspense>
    );
};
